import React from 'react'
import { Flex, Text, Tokens } from '@grammarly/design-system'

import { detectedAIColor, noDetectedAIcolor } from '@components/tool/constants'

import styles from './ai-detector.module.scss'

const minPercentage = 0
const maxPercentage = 100
const progressIndicatorId = 'ai-detector-progress-indicator'

interface ProgressIndicatorProps {
  percentage: number | null
  size?: number
  strokeWidth?: number
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ percentage, size = 165, strokeWidth = 12 }) => {
  const radius = (size - strokeWidth) / 2
  const circumference = Math.PI * radius

  const height = (size + strokeWidth) / 2

  if (percentage === null) {
    return (
      <Flex direction="row" align="center" justify="center" className={styles.progressIndicator}>
        <div
          role="meter"
          aria-valuenow={0}
          aria-valuemin={minPercentage}
          aria-valuemax={maxPercentage}
          aria-labelledby={progressIndicatorId}
        >
          <svg width={size} height={height} viewBox={`0 0 ${size} ${height}`}>
            <path
              d={`M ${size / 2 + radius} ${size / 2} A ${radius} ${radius} 0 0 0 ${size / 2 - radius} ${size / 2}`}
              fill="none"
              stroke={Tokens.Color.NeutralGray30}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              className={styles.remainingArc}
            />
          </svg>
        </div>
        <div className={styles.percentageText} id={progressIndicatorId}>
          <Text variant="heading-large" as="span" color="base-subdued">
            {
              // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              '—'
            }
          </Text>
          <Text variant="heading-medium" as="span" color="base-subdued">
            {
              // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              '%'
            }
          </Text>
        </div>
      </Flex>
    )
  }

  // 1,2 and 98, and 99 percent don't render well, so we need to adjust the percentage visually.
  const progressPercentage = percentage >= 98 ? 100 : percentage <= 2 ? 0 : percentage
  const progressLength = (progressPercentage / 100) * circumference

  const remainingLength = circumference - progressLength

  const arcSeparation = progressPercentage === 0 || progressPercentage === 100 ? 0 : (strokeWidth * 2) / 3

  return (
    <Flex direction="row" align="center" justify="center" className={styles.progressIndicator}>
      <div
        role="meter"
        aria-valuenow={percentage}
        aria-valuemin={minPercentage}
        aria-valuemax={maxPercentage}
        aria-labelledby={progressIndicatorId}
      >
        <svg width={size} height={height} viewBox={`0 0 ${size} ${height}`}>
          {percentage > 2 && (
            <path
              d={`M ${size / 2 - radius} ${size / 2} A ${radius} ${radius} 0 0 1 ${size / 2 + radius} ${size / 2}`}
              fill="none"
              stroke={detectedAIColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={`${progressLength - arcSeparation} ${remainingLength + arcSeparation}`}
              className={styles.progressArc}
            />
          )}

          {percentage < 98 && (
            <path
              d={`M ${size / 2 + radius} ${size / 2} A ${radius} ${radius} 0 0 0 ${size / 2 - radius} ${size / 2}`}
              fill="none"
              stroke={noDetectedAIcolor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={`${remainingLength - arcSeparation} ${progressLength + arcSeparation}`}
              className={styles.remainingArc}
            />
          )}
        </svg>
      </div>
      <div className={styles.percentageText} id={progressIndicatorId}>
        <Text variant="heading-large" as="span" color="base-default">
          {percentage}
        </Text>
        <Text variant="heading-medium" as="span" color="base-default">
          {
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            '%'
          }
        </Text>
      </div>
    </Flex>
  )
}
